import axios, { AxiosResponse } from "axios";
import { handleApiError } from "../../../../common/utils";
import {
  ICSIncident,
  ICSRole,
  ICSDecisionLog,
  ICSFormData,
  ICSDecisionFormData,
  ICSApprovalFormData,
  ICSStatus,
  ApiResponse,
} from "../types/types";
import axiosInstance from "../../../../common/axiosInstance";



// ============================================================================
// ICS INCIDENT SERVICES
// ============================================================================

/**
 * Fetch all ICS incidents
 */
export const fetchICSIncidents = async (params?: {
  status?: string;
  search?: string;
  limit?: number;
  skip?: number;
}): Promise<ApiResponse<ICSIncident[]>> => {
  try {
    let url = `/api/ics/incidents`; // Make sure this matches your route

    if (params) {
      const queryParams = new URLSearchParams();
      if (params.status) queryParams.append("status", params.status);
      if (params.search) queryParams.append("search", params.search);
      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.skip) queryParams.append("skip", params.skip.toString());

      if (queryParams.toString()) {
        url += `?${queryParams.toString()}`;
      }
    }

    const response: AxiosResponse<ApiResponse<ICSIncident[]>> =
      await axiosInstance.get(url);
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch ICS incidents");
    throw error;
  }
};

/**
 * Get single ICS incident by ID
 */
export const getICSIncidentById = async (
  id: string,
): Promise<ApiResponse<ICSIncident>> => {
  try {
    const response: AxiosResponse<ApiResponse<ICSIncident>> = await axiosInstance.get(
      `/api/ics/incidents/${id}`,
      {
        },
    );

    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch ICS incident");
    throw error;
  }
};

/**
 * Create new ICS incident
 */
export const createICSIncident = async (
  icsData: ICSFormData,
): Promise<ApiResponse<ICSIncident>> => {
  try {
    const response: AxiosResponse<ApiResponse<ICSIncident>> = await axiosInstance.post(
      `/api/ics/incidents`,
      icsData,
      {
        },
    );

    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to create ICS incident");
    throw error;
  }
};

/**
 * Update ICS incident
 */
export const updateICSIncident = async (
  id: string,
  icsData: Partial<ICSFormData> & { status?: ICSStatus; notes?: string },
): Promise<ApiResponse<ICSIncident>> => {
  try {
    const response: AxiosResponse<ApiResponse<ICSIncident>> = await axiosInstance.put(
      `/api/ics/incidents/${id}`,
      icsData,
      {
        },
    );

    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to update ICS incident");
    throw error;
  }
};

/**
 * Transfer incident from Incident Management to ICS
 */
export const transferIncidentToICS = async (
  incidentId: string,
  roles: { roleType: string; assignedUserId: string }[],
  department?: string,
): Promise<ApiResponse<ICSIncident>> => {
  try {
    const response: AxiosResponse<ApiResponse<ICSIncident>> = await axiosInstance.post(
      `/api/ics/incidents/transfer`,
      {
        incidentId,
        roles,
        department: department ?? null,
      },
      {
        },
    );

    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to transfer incident to ICS");
    throw error;
  }
};

// ============================================================================
// ICS ROLE SERVICES
// ============================================================================

/**
 * Get roles for an ICS incident (supports "regular-<id>" for non-transferred incidents)
 */
export const getICSRoles = async (
  icsIncidentId: string,
): Promise<ApiResponse<ICSRole[]>> => {
  try {
    const response: AxiosResponse<ApiResponse<ICSRole[]>> = await axiosInstance.get(
      `/api/ics/incidents/${icsIncidentId}/roles`,
      {
        },
    );
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch ICS roles");
    throw error;
  }
};

/**
 * Assign ICS role
 */
export const assignICSRole = async (
  icsIncidentId: string,
  roleData: {
    roleType: string;
    assignedUserId: string;
    notes?: string;
  },
): Promise<ApiResponse<ICSRole>> => {
  try {
    const response: AxiosResponse<ApiResponse<ICSRole>> = await axiosInstance.post(
      `/api/ics/incidents/${icsIncidentId}/roles`,
      roleData,
      {
        },
    );

    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to assign ICS role");
    throw error;
  }
};

/**
 * Update ICS role assignment
 */
export const updateICSRole = async (
  icsIncidentId: string,
  roleId: string,
  roleData: {
    assignedUserId?: string;
    status?: string;
    notes?: string;
  },
): Promise<ApiResponse<ICSRole>> => {
  try {
    const response: AxiosResponse<ApiResponse<ICSRole>> = await axiosInstance.put(
      `/api/ics/incidents/${icsIncidentId}/roles/${roleId}`,
      roleData,
      {
        },
    );

    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to update ICS role");
    throw error;
  }
};

/**
 * Remove ICS role assignment
 */
export const removeICSRole = async (
  icsIncidentId: string,
  roleId: string,
): Promise<ApiResponse<void>> => {
  try {
    const response: AxiosResponse<ApiResponse<void>> = await axiosInstance.delete(
      `/api/ics/incidents/${icsIncidentId}/roles/${roleId}`,
      {
        },
    );

    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to remove ICS role");
    throw error;
  }
};

// ============================================================================
// ICS DECISION LOG SERVICES
// ============================================================================

/**
 * Create decision log entry
 */
export const createDecisionLog = async (
  icsIncidentId: string,
  decisionData: ICSDecisionFormData,
): Promise<ApiResponse<ICSDecisionLog>> => {
  try {
    const formData = new FormData();
    formData.append("incidentId", decisionData.incidentId);
    formData.append("decisionTitle", decisionData.decisionTitle);
    formData.append("decisionDescription", decisionData.decisionDescription);
    formData.append("decisionType", decisionData.decisionType);
    formData.append("priority", decisionData.priority);
    formData.append("relatedRoles", JSON.stringify(decisionData.relatedRoles));

    if (decisionData.attachments) {
      decisionData.attachments.forEach((file) => {
        formData.append("attachments", file);
      });
    }

    const response: AxiosResponse<ApiResponse<ICSDecisionLog>> =
      await axiosInstance.post(
        `/api/ics/incidents/${icsIncidentId}/decisions`,
        formData,
        {
          },
      );

    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to create decision log");
    throw error;
  }
};

/**
 * Get decision logs for an ICS incident
 */
export const getDecisionLogs = async (
  icsIncidentId: string,
): Promise<ApiResponse<ICSDecisionLog[]>> => {
  try {
    const response: AxiosResponse<ApiResponse<ICSDecisionLog[]>> =
      await axiosInstance.get(
        `/api/ics/incidents/${icsIncidentId}/decisions`,
        {
          },
      );

    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch decision logs");
    throw error;
  }
};

/**
 * Approve or reject a decision
 */
export const approveDecision = async (
  icsIncidentId: string,
  decisionId: string,
  approvalData: ICSApprovalFormData,
): Promise<ApiResponse<ICSDecisionLog>> => {
  try {
    const response: AxiosResponse<ApiResponse<ICSDecisionLog>> =
      await axiosInstance.put(
        `/api/ics/incidents/${icsIncidentId}/decisions/${decisionId}/approve`,
        approvalData,
        {
          },
      );

    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to process decision approval");
    throw error;
  }
};

/**
 * Update decision log
 */
export const updateDecisionLog = async (
  icsIncidentId: string,
  decisionId: string,
  decisionData: Partial<ICSDecisionFormData>,
): Promise<ApiResponse<ICSDecisionLog>> => {
  try {
    const formData = new FormData();
    if (decisionData.decisionTitle)
      formData.append("decisionTitle", decisionData.decisionTitle);
    if (decisionData.decisionDescription)
      formData.append("decisionDescription", decisionData.decisionDescription);
    if (decisionData.decisionType)
      formData.append("decisionType", decisionData.decisionType);
    if (decisionData.priority)
      formData.append("priority", decisionData.priority);
    if (decisionData.relatedRoles)
      formData.append(
        "relatedRoles",
        JSON.stringify(decisionData.relatedRoles),
      );

    if (decisionData.attachments) {
      decisionData.attachments.forEach((file) => {
        formData.append("attachments", file);
      });
    }

    const response: AxiosResponse<ApiResponse<ICSDecisionLog>> =
      await axiosInstance.put(
        `/api/ics/incidents/${icsIncidentId}/decisions/${decisionId}`,
        formData,
        {
          },
      );

    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to update decision log");
    throw error;
  }
};

/**
 * Download bulk ICS incidents as Excel file
 */
export const downloadBulkICSIncidentsExcel = async (params?: {
  fromDate?: string;
  toDate?: string;
  department?: string;
}): Promise<Blob> => {
  try {
    let url = `/api/ics/incidents/export/bulk`;

    if (params) {
      const queryParams = new URLSearchParams();
      if (params.fromDate) queryParams.append("startDate", params.fromDate);
      if (params.toDate) queryParams.append("endDate", params.toDate);
      if (params.department)
        queryParams.append("department", params.department);

      if (queryParams.toString()) {
        url += `?${queryParams.toString()}`;
      }
    }

    const response: AxiosResponse<Blob> = await axiosInstance.get(url, {
      responseType: "blob",
    });

    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to download bulk ICS incidents report");
    throw error;
  }
};
